import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions, base } from '../../base';
import '../../styles/css/AdminMain.css';
import { Toast, ErrorMessage, WarningMessage } from '../utils/HelpfulFunction';
import { experiencePath, getScope, subscribeToScope, registerScopeGuard } from '../utils/experienceScope';

// Available Ionicons for tab bar
const AVAILABLE_ICONS = [
    { name: 'home', label: 'Home' },
    { name: 'calendar', label: 'Calendar' },
    { name: 'people', label: 'People' },
    { name: 'stats-chart', label: 'Stats' },
    { name: 'menu', label: 'Menu' },
    { name: 'ticket', label: 'Ticket' },
    { name: 'newspaper', label: 'News' },
    { name: 'trophy', label: 'Trophy' },
    { name: 'settings', label: 'Settings' },
    { name: 'information-circle', label: 'Info' },
    { name: 'cart', label: 'Cart/Shop' },
    { name: 'heart', label: 'Heart' },
    { name: 'star', label: 'Star' },
    { name: 'gift', label: 'Gift' },
    { name: 'notifications', label: 'Notifications' },
    { name: 'person', label: 'Person' },
    { name: 'search', label: 'Search' },
    { name: 'play', label: 'Play' },
    { name: 'musical-notes', label: 'Music' },
    { name: 'radio', label: 'Radio' },
];

class SetUpBottomTabs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tabsList: [],
            loading: true,
            localTabsList: [],
            hasUnsavedChanges: false,
            modal: false,
            tabLabel: '',
            tabIcon: 'home',
            tabLink: '',
            buttonType: 'isScreen',
            screenDestination: 'Home',
            openOutsideApp: false,
            editingTab: false,
            tabToEdit: null,
            enabledModules: {},
            // The working scope, owned by utils/experienceScope: a ?experience=
            // in the URL wins, else this admin's stored choice, else '' for the
            // default experience. The top-bar switcher drives it.
            selectedExperience: getScope()
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.saveToDatabase = this.saveToDatabase.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        this.bindData = this.bindData.bind(this);
        this.handleExperienceChange = this.handleExperienceChange.bind(this);
    }

    async componentDidMount() {
        this.unsubscribeScope = subscribeToScope(id => this.handleExperienceChange(id));
        this.unregisterScopeGuard = registerScopeGuard(() => !this.state.hasUnsavedChanges);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        await this.bindData();
    }

    async bindData() {
        const { selectedExperience } = this.state;

        // Fetch enabled modules
        const modulesSnapshot = await appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'settings/modules')).once('value');
        const modules = modulesSnapshot.val() || {};
        this.setState({ enabledModules: modules });

        // Listen to bottom tabs configuration
        this.bottomTabsRef = base.listenTo(experiencePath(selectedExperience, 'settings/bottomTabs/tabs'), {
            context: this,
            state: 'tabsList',
            asArray: true,
            then(data = []) {
                // Sort by order
                const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
                this.setState({
                    loading: false,
                    tabsList: sorted,
                    localTabsList: sorted
                });
            },
            catch(e) {
                this.setState({ loading: false });
            }
        });
    }

    handleExperienceChange(experienceId) {
        // Detach the current experience's binding before re-binding to the new one
        base.removeBinding(this.bottomTabsRef);
        this.setState({
            selectedExperience: experienceId,
            tabsList: [],
            localTabsList: [],
            enabledModules: {},
            hasUnsavedChanges: false,
            loading: true
        }, () => this.bindData());
    }

    componentWillUnmount() {
        if (this.unsubscribeScope) this.unsubscribeScope();
        if (this.unregisterScopeGuard) this.unregisterScopeGuard();
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        base.removeBinding(this.bottomTabsRef);
    }

    handleBeforeUnload(event) {
        if (this.state.hasUnsavedChanges) {
            const message = 'You have unsaved changes. Are you sure you want to leave?';
            event.returnValue = message;
            return message;
        }
    }

    handleChange(event) {
        const { name, type, checked, value } = event.target;
        this.setState({ [name]: type === 'checkbox' ? checked : value });
    }

    handleSubmit(event) {
        event.preventDefault();
        if (this.state.loading) return;

        const { tabLabel, tabIcon, buttonType, tabLink, screenDestination, openOutsideApp, localTabsList, tabToEdit } = this.state;

        if (!tabLabel || tabLabel.length > 20) {
            ErrorMessage.fire({
                title: 'Invalid Label',
                text: 'Label cannot be empty or longer than 20 characters'
            });
            return;
        }

        const tabToSave = {
            id: (tabToEdit && tabToEdit.id) || appDatabasePrimaryFunctions.ref().push().key,
            label: tabLabel,
            icon: tabIcon,
            buttonType: buttonType,
            order: (tabToEdit && tabToEdit.order) || localTabsList.length + 1,
            visible: true
        };

        // Add type-specific fields
        if (buttonType === 'isScreen') {
            tabToSave.screen = screenDestination;
        } else if (buttonType === 'isWebsiteLink') {
            tabToSave.link = tabLink;
            tabToSave.openOutsideApp = openOutsideApp;
        }

        const updatedTabsList = [...localTabsList];
        if (this.state.editingTab) {
            const index = updatedTabsList.findIndex(tab => tab.id === tabToEdit.id);
            updatedTabsList[index] = tabToSave;
        } else {
            updatedTabsList.push(tabToSave);
        }

        this.setState({
            localTabsList: updatedTabsList,
            modal: false,
            tabLabel: '',
            tabIcon: 'home',
            tabLink: '',
            buttonType: 'isScreen',
            screenDestination: 'Home',
            openOutsideApp: false,
            editingTab: false,
            tabToEdit: null,
            hasUnsavedChanges: true
        });

        Toast.fire({ title: this.state.editingTab ? 'Tab Updated!' : 'Tab Added!' });
    }

    editTab(index) {
        const tab = this.state.localTabsList[index];
        this.setState({
            modal: true,
            tabLabel: tab.label,
            tabIcon: tab.icon || 'home',
            buttonType: tab.buttonType || 'isScreen',
            screenDestination: tab.screen || 'Home',
            tabLink: tab.link || '',
            openOutsideApp: tab.openOutsideApp || false,
            editingTab: true,
            tabToEdit: tab
        });
    }

    async deleteTab(index) {
        const response = await WarningMessage.fire({
            title: 'Delete Tab?',
            text: 'Are you sure you want to delete this tab?',
            confirmButtonText: 'Delete'
        });
        if (response.value) {
            const updatedTabsList = this.state.localTabsList.filter((_, i) => i !== index);
            // Reorder remaining tabs
            updatedTabsList.forEach((tab, i) => { tab.order = i + 1; });
            this.setState({ localTabsList: updatedTabsList, hasUnsavedChanges: true });
            Toast.fire({ title: 'Tab Deleted!' });
        }
    }

    toggle() {
        this.setState({
            modal: !this.state.modal,
            tabLabel: '',
            tabIcon: 'home',
            tabLink: '',
            buttonType: 'isScreen',
            screenDestination: 'Home',
            openOutsideApp: false,
            editingTab: false,
            tabToEdit: null
        });
    }

    changeOrder(index, direction) {
        const tabsList = [...this.state.localTabsList];
        const newIndex = index + direction;

        if (newIndex < 0 || newIndex >= tabsList.length) return;

        // Swap items
        [tabsList[index], tabsList[newIndex]] = [tabsList[newIndex], tabsList[index]];

        // Update order values
        tabsList.forEach((tab, i) => { tab.order = i + 1; });

        this.setState({ localTabsList: tabsList, hasUnsavedChanges: true });
    }

    toggleVisibility(index) {
        const tabsList = [...this.state.localTabsList];
        tabsList[index].visible = !tabsList[index].visible;
        this.setState({ localTabsList: tabsList, hasUnsavedChanges: true });
    }

    saveToDatabase() {
        const { localTabsList } = this.state;

        // Create object for Firebase
        const tabsObject = {};
        localTabsList.forEach(tab => {
            tabsObject[tab.id] = { ...tab };
        });

        appDatabasePrimaryFunctions.ref(experiencePath(this.state.selectedExperience, 'settings/bottomTabs/tabs')).set(tabsObject)
            .then(() => {
                Toast.fire({ title: 'Saved Successfully!' });
                this.setState({ hasUnsavedChanges: false });
            })
            .catch(err => {
                ErrorMessage.fire({
                    title: 'Error Saving',
                    text: 'Please try again'
                });
                console.log(err);
            });
    }

    getButtonTypeLabel(buttonType) {
        const labels = {
            'isScreen': 'Screen',
            'isWebsiteLink': 'Website Link',
            'isSideMenu': 'Open Side Menu',
            'isTicketMaster': 'Ticketmaster',
            'isCheckIn': 'Check In',
            'isPlayerPick': 'Player Pick',
            'isBadges': 'Badges',
            'isPrizes': 'Rewards',
            'isRadio': 'Radio',
            'isCueAudio': 'CUE Audio'
        };
        return labels[buttonType] || buttonType;
    }

    render() {
        const { loading, localTabsList, hasUnsavedChanges, buttonType } = this.state;

        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: loading ? 'block' : 'none' }} />
                <SideMenu />
                <TopMenu />
                <div className="admin-main-panel">
                    <div className="card">
                        <div className="card-body">
                            <div className="admin-page-head" style={{ margin: 0 }}>
                                <p className="admin-eyebrow">App layout</p>
                                <p className="admin-header-text">Bottom Tabs</p>
                                <p className="admin-page-sub">The navigation bar along the bottom of your app, in the order fans see it.</p>
                            </div>


                            <div className="admin-actions">
                                <button className="btn btn-primary" onClick={this.saveToDatabase}>Save changes</button>
                                <button className="btn btn-secondary" onClick={this.toggle}>Add tab</button>
                                {hasUnsavedChanges && (
                                    <span className="admin-unsaved">You have unsaved changes</span>
                                )}
                            </div>

                            <div className="row" style={{ marginTop: 20 }}>
                                <div className="col-md-12">
                                    {/* Header row */}
                                    <div style={{ display: 'flex', padding: '10px 15px', backgroundColor: '#f5f5f5', borderRadius: '4px 4px 0 0', fontWeight: 'bold', fontSize: 12, color: '#666' }}>
                                        <div style={{ width: 60, textAlign: 'center' }}>Order</div>
                                        <div style={{ width: 60, textAlign: 'center' }}>Visible</div>
                                        <div style={{ width: 150 }}>Label</div>
                                        <div style={{ width: 100 }}>Icon</div>
                                        <div style={{ flex: 1 }}>Type / Destination</div>
                                        <div style={{ width: 200 }}>Actions</div>
                                    </div>

                                    <ul className="list-group" style={{ color: 'black' }}>
                                        {localTabsList.map((tab, i) => (
                                            <li key={tab.id} className="list-group-item" style={{ opacity: tab.visible === false ? 0.5 : 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ width: 60, textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="admin-order-btn"
                                                            aria-label={`Move ${tab.label || 'tab'} up`}
                                                            disabled={i === 0}
                                                            style={{ marginRight: 4 }}
                                                            onClick={() => i > 0 && this.changeOrder(i, -1)}
                                                        ><span className="fa fa-chevron-up"/></button>
                                                        <button
                                                            type="button"
                                                            className="admin-order-btn"
                                                            aria-label={`Move ${tab.label || 'tab'} down`}
                                                            disabled={i >= localTabsList.length - 1}
                                                            onClick={() => i < localTabsList.length - 1 && this.changeOrder(i, 1)}
                                                        ><span className="fa fa-chevron-down"/></button>
                                                    </div>
                                                    <div style={{ width: 60, textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={tab.visible !== false}
                                                            onChange={() => this.toggleVisibility(i)}
                                                            style={{ width: 18, height: 18 }}
                                                        />
                                                    </div>
                                                    <div style={{ width: 150, fontWeight: 'bold' }}>{tab.label}</div>
                                                    <div style={{ width: 100 }}>{tab.icon}</div>
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{ color: '#666' }}>{this.getButtonTypeLabel(tab.buttonType)}</span>
                                                        {tab.screen && <span style={{ marginLeft: 5 }}>→ {tab.screen}</span>}
                                                        {tab.link && <span style={{ marginLeft: 5, fontSize: 12, color: '#999' }}>({tab.link})</span>}
                                                    </div>
                                                    <div style={{ width: 200 }}>
                                                        <button type="button" className="admin-icon-btn" aria-label="Edit tab" title="Edit tab" onClick={() => this.editTab(i)}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                        <button type="button" className="admin-icon-btn is-danger" aria-label="Delete tab" title="Delete tab" onClick={() => this.deleteTab(i)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    {localTabsList.length === 0 && (
                                        <div className="admin-empty">
                                            <p className="admin-empty-title">No tabs yet</p>
                                            <p className="admin-empty-body">Add a tab to build the navigation bar along the bottom of your app.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Modal isOpen={this.state.modal} toggle={this.toggle} style={{ width: '90%' }} id="myModal">
                    <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }} />
                    <ModalHeader toggle={this.toggle}>{this.state.editingTab ? 'Edit Tab' : 'Add Tab'}</ModalHeader>
                    <ModalBody>
                        <div className="container-out">
                            <div className="question-box question-form">
                                <form className="pl-3 pr-3" onSubmit={this.handleSubmit}>
                                    <div className="form-group">
                                        <label htmlFor="tabLabel">Label (max 20 characters)</label>
                                        <input
                                            id="tabLabel"
                                            name="tabLabel"
                                            type="text"
                                            className="form-control"
                                            value={this.state.tabLabel}
                                            onChange={this.handleChange}
                                            placeholder="Tab Label"
                                            maxLength={20}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="tabIcon">Icon</label>
                                        <select
                                            id="tabIcon"
                                            name="tabIcon"
                                            className="form-control"
                                            value={this.state.tabIcon}
                                            onChange={this.handleChange}
                                        >
                                            {AVAILABLE_ICONS.map(icon => (
                                                <option key={icon.name} value={icon.name}>{icon.label} ({icon.name})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-12 mb-3">
                                        <label>Tab Type</label>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input
                                                        id="isScreen"
                                                        name="buttonType"
                                                        type="radio"
                                                        className="form-check-input"
                                                        value="isScreen"
                                                        checked={buttonType === 'isScreen'}
                                                        onChange={this.handleChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="isScreen">Screen</label>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input
                                                        id="isWebsiteLink"
                                                        name="buttonType"
                                                        type="radio"
                                                        className="form-check-input"
                                                        value="isWebsiteLink"
                                                        checked={buttonType === 'isWebsiteLink'}
                                                        onChange={this.handleChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="isWebsiteLink">Website Link</label>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-check">
                                                    <input
                                                        id="isSideMenu"
                                                        name="buttonType"
                                                        type="radio"
                                                        className="form-check-input"
                                                        value="isSideMenu"
                                                        checked={buttonType === 'isSideMenu'}
                                                        onChange={this.handleChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="isSideMenu">Open Side Menu</label>
                                                </div>
                                            </div>
                                            {process.env.REACT_APP_HAS_TICKETMASTER === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isTicketMaster"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isTicketMaster"
                                                            checked={buttonType === 'isTicketMaster'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isTicketMaster">Ticketmaster</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_CHECK_IN === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isCheckIn"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isCheckIn"
                                                            checked={buttonType === 'isCheckIn'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isCheckIn">Check In</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_PLAYER_PICK === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isPlayerPick"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isPlayerPick"
                                                            checked={buttonType === 'isPlayerPick'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isPlayerPick">Player Pick</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_BADGES === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isBadges"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isBadges"
                                                            checked={buttonType === 'isBadges'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isBadges">Badges</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_PRIZES === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isPrizes"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isPrizes"
                                                            checked={buttonType === 'isPrizes'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isPrizes">Rewards</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_RADIO === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isRadio"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isRadio"
                                                            checked={buttonType === 'isRadio'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isRadio">Radio</label>
                                                    </div>
                                                </div>
                                            )}
                                            {process.env.REACT_APP_HAS_CUE_AUDIO === 'true' && (
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="isCueAudio"
                                                            name="buttonType"
                                                            type="radio"
                                                            className="form-check-input"
                                                            value="isCueAudio"
                                                            checked={buttonType === 'isCueAudio'}
                                                            onChange={this.handleChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="isCueAudio">CUE Audio</label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {buttonType === 'isScreen' && (
                                        <div className="form-group">
                                            <label htmlFor="screenDestination">Screen Destination</label>
                                            <select
                                                id="screenDestination"
                                                name="screenDestination"
                                                className="form-control"
                                                value={this.state.screenDestination}
                                                onChange={this.handleChange}
                                            >
                                                <option value="Home">Home</option>
                                                {this.state.enabledModules.schedule?.enabled && (
                                                    <option value="Schedule">Schedule</option>
                                                )}
                                                {this.state.enabledModules.roster?.enabled && (
                                                    <option value="Roster">Roster</option>
                                                )}
                                                {(this.state.enabledModules.standings?.enabled || this.state.enabledModules.playerLeaders?.enabled) && (
                                                    <option value="Stats">Stats</option>
                                                )}
                                            </select>
                                        </div>
                                    )}

                                    {buttonType === 'isWebsiteLink' && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="tabLink">URL</label>
                                                <input
                                                    id="tabLink"
                                                    name="tabLink"
                                                    type="url"
                                                    className="form-control"
                                                    value={this.state.tabLink}
                                                    onChange={this.handleChange}
                                                    placeholder="https://example.com"
                                                />
                                            </div>
                                            <div className="form-check mb-3">
                                                <input
                                                    id="openOutsideApp"
                                                    name="openOutsideApp"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={this.state.openOutsideApp}
                                                    onChange={this.handleChange}
                                                />
                                                <label className="form-check-label" htmlFor="openOutsideApp">
                                                    Open Outside App
                                                </label>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-group text-center">
                                        <button className="btn btn-primary btn-lg submit-button" id="submitButton">
                                            {this.state.editingTab ? 'Update Tab' : 'Add Tab'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

export default SetUpBottomTabs;
